import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import { useIsBetSlipSubmitting } from '@/feature/betslip-sbk/hooks/betslip-hooks';
import { BetSubmissionStatus } from '@/feature/betslip-sbk/types';
import { runAfterInteractions } from '@/utils/runAfterInteractions';
import { runStoreUpdate } from '@/utils/zustand';

import { usePicksRemovedModal } from '../components/picks-removed-modal/PicksRemovedModalProvider';
import { useSbkBetSlipStore } from '../hooks/use-sbk-betslip-store';

const TOAST_DURATION = 5000;

/**
 * Hook to handle closed selections in the betslip.
 * - On mount (betslip open): shows PicksRemovedModal for any accumulated closed selections
 * - After mount (live): if modal is visible, new selections appear in the modal automatically;
 *   otherwise shows a toast
 */
export const useClosedSelectionsToast = () => {
    const { showToast, clearAllToasts } = useAlerts();
    const closedSelectionsCount = useSbkBetSlipStore(state => state.closedSelections.length);
    const clearClosedSelections = useSbkBetSlipStore(state => state.actions.clearClosedSelections);
    const removeClosedSelections = useSbkBetSlipStore(state => state.actions.removeClosedSelections);
    const isBetSlipSubmitting = useIsBetSlipSubmitting();
    const isBetSubmissionSuccess = useSbkBetSlipStore(
        state => state.betSubmissionStatus === BetSubmissionStatus.Success
    );
    const isModalVisible = useRef(false);
    const toastTimeout = useRef<ReturnType<typeof setTimeout>>(null);
    const isInitialMount = useRef(true);
    const { t } = useTranslation('betslip_sbk');
    const { showPicksRemovedModal } = usePicksRemovedModal();

    useEffect(() => {
        const isInitial = isInitialMount.current;
        isInitialMount.current = false;

        if (closedSelectionsCount > 0 && !isBetSlipSubmitting) {
            // Always remove closed selections from betslip
            runStoreUpdate(() => removeClosedSelections());

            if (isInitial) {
                // Accumulated selections on betslip open — show modal
                isModalVisible.current = true;
                runAfterInteractions(() => {
                    showPicksRemovedModal(() => {
                        isModalVisible.current = false;
                        clearClosedSelections();
                    });
                });
            } else if (!isModalVisible.current) {
                // Live closed selection while betslip is open — show toast
                if (isBetSubmissionSuccess) {
                    clearClosedSelections();
                    return;
                }

                showToast(
                    {
                        message: t('closed_picks_toast_message', {
                            count: closedSelectionsCount,
                        }),
                        toastType: 'warning',
                        positionY: 168,
                        onPress: () => {
                            isModalVisible.current = true;
                            clearAllToasts();
                            showPicksRemovedModal(() => {
                                isModalVisible.current = false;
                                clearClosedSelections();
                            });
                        },
                    },
                    TOAST_DURATION
                );

                if (toastTimeout.current) {
                    clearTimeout(toastTimeout.current);
                }
                toastTimeout.current = setTimeout(() => {
                    if (!isModalVisible.current) {
                        clearClosedSelections();
                    }
                }, TOAST_DURATION);
            }
        }
    }, [
        t,
        clearClosedSelections,
        closedSelectionsCount,
        showToast,
        clearAllToasts,
        isBetSlipSubmitting,
        isBetSubmissionSuccess,
        removeClosedSelections,
        showPicksRemovedModal,
    ]);

    // Clear pending toast timeout on unmount to prevent stale clearClosedSelections
    useEffect(() => {
        return () => {
            if (toastTimeout.current) {
                clearTimeout(toastTimeout.current);
            }
        };
    }, []);
};
